import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { API } from '../../api/api';
import { Permission } from '../../model/auth.model';

export interface RoleListParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

interface RolePermissionApiItem {
  id: number;
  name: string;
  displayName?: string | null;
  isSelected: boolean;
}

interface RolePermissionApiGroup {
  category: string;
  permissions: RolePermissionApiItem[];
}

interface RolePermissionsResponse {
  rolePermissions?: number[];
  permissions: RolePermissionApiGroup[];
}

@Injectable({ providedIn: 'root' })
export class RolesService {
  private readonly http = inject(HttpClient);

  getRoles(query: RoleListParams = {}): Observable<unknown> {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get(API.ROLES.LIST, { params });
  }

  createRole(payload: { name: string; description?: string }): Observable<unknown> {
    return this.http.post(API.ROLES.CREATE, payload);
  }

  updateRole(id: number, payload: { name: string; description?: string }): Observable<unknown> {
    return this.http.patch(API.ROLES.UPDATE(id), payload);
  }

  deleteRole(id: number): Observable<unknown> {
    return this.http.delete(API.ROLES.DELETE(id));
  }

  getRolePermissions(id: number): Observable<unknown> {
    return this.http.get(API.ROLES.PERMISSIONS(id));
  }

  getSelectedRolePermissions(id: number): Observable<Permission[]> {
    return this.http.get<RolePermissionsResponse>(API.ROLES.PERMISSIONS(id)).pipe(
      map((response) => {
        const val = (response as any)?.data ?? response ?? {};
        const selectedPermissionIds = new Set(
          (val.rolePermissions ?? []).map((pId: unknown) => Number(pId)),
        );

        return (val.permissions ?? []).flatMap((group: any) => {
          let categoryName = String(group.category ?? group.name ?? '').trim();
          if (
            categoryName.toLowerCase() === 'geofencing' ||
            categoryName.toLowerCase() === 'geofence'
          ) {
            categoryName = 'Zone';
          }
          return (group.permissions ?? [])
            .filter(
              (permission: any) =>
                permission.isSelected || selectedPermissionIds.has(Number(permission.id)),
            )
            .map((permission: any) => ({
              id: Number(permission.id),
              code: permission.name,
              module: categoryName,
              displayName: permission.displayName,
            }));
        });
      }),
    );
  }

  assignPermissions(
    id: number,
    payload: { roleName: string; roleDescription?: string; permissionIds: number[] },
  ): Observable<unknown> {
    return this.http.put(API.ROLES.PERMISSIONS(id), payload);
  }
}
